/**
 * useAppState — CUADRO NEGRO División Pragmática App.js
 *
 * Centraliza los estados de UI globales del componente Home() y sus
 * listeners self-contained. NO contiene lógica del chat ni de imágenes.
 *
 * Exports:
 *   mobileView, setMobileView
 *   chatPanelExpanded, setChatPanelExpanded
 *   workspaceExpanded, setWorkspaceExpanded
 *   forceInternalWorkspace, setForceInternalWorkspace
 *   showImageEditor, setShowImageEditor
 *   activeModuleId
 *   swipeHandlers
 */
import { useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
// 🟥 CUADRO NEGRO 2026-06-29 (rev12) — `showImageEditor` también vive
// en el store global del Workspace (vía Provider en index.js) para
// estar sincronizado con `latestWorkspaceImage` y sobrevivir a re-mounts.
// Antes era un `useState(false)` local que se sobre-escribía al state
// del store y bloqueaba el render del WorkspaceImageViewer post-publish.
import { useWorkspaceImageStore } from '../../context/WorkspaceImageContext';

export default function useAppState() {
  // Navegación móvil entre Chat y Área de Trabajo
  const [mobileView, setMobileView] = useState('chat'); // 'chat' | 'workspace'

  // Swipe handlers para navegación móvil
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setMobileView('workspace'),
    onSwipedRight: () => setMobileView('chat'),
    preventScrollOnSwipe: true,
    trackMouse: false,
    trackTouch: true,
    delta: 50,
    swipeDuration: 500,
  });

  // Expand del Chat / Workspace (desktop)
  const [chatPanelExpanded, setChatPanelExpanded] = useState(false);
  const [workspaceExpanded, setWorkspaceExpanded] = useState(false);

  // Forzar la apertura del InternalWorkspace
  const [forceInternalWorkspace, setForceInternalWorkspace] = useState(false);

  // Editor de imagen del Workspace — DELEGADO al store global
  // (mismo Provider que provee latestWorkspaceImage). Esto garantiza que
  // cuando llega `workspace-image-published`, BOTH `latestWorkspaceImage`
  // y `showImageEditor` se actualicen como una única transacción del store.
  const { showImageEditor, setShowImageEditor } = useWorkspaceImageStore();

  // FASE 11: Workspace ↔ Chat sync — ID del módulo abierto en el
  // InternalWorkspace. Se setea cuando el usuario selecciona un módulo
  // (event 'damyxia:active-module-changed').
  const [activeModuleId, setActiveModuleId] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.moduleId || null;
      const reason = e.detail?.reason;
      const source = e.detail?.source;
      // 🟥 CUADRO NEGRO MAESTRO (sec 6 y 8) — Estados prohibidos:
      //  · El Builder (Sistema B) no puede cambiar el item activo.
      //  · El proyecto activo NO se cierra sin orden explícita: un reset a
      //    null sólo se acepta si el usuario cerró el trabajo o lo borró.
      if (source === 'builder') {
        console.warn('[CUADRO NEGRO] acción del Builder sobre el Workspace BLOQUEADA');
        return;
      }
      if (!id && reason !== 'user-close' && reason !== 'deleted') {
        console.warn('[CUADRO NEGRO] reset del proyecto activo IGNORADO (sin orden explícita)');
        return;
      }
      setActiveModuleId(id);
      // 🟥 iter437 · Sync a window/localStorage para que otros módulos (ej.
      // MessageBubble) puedan leer el activo sin acceder al closure de React.
      // Se usa en el guard defense-in-depth que bloquea fences type:"task"/
      // "note"/"document" cuando hay proyecto activo.
      try {
        if (typeof window !== 'undefined') {
          window.__damyxiaActiveModuleId = id || null;
        }
        if (typeof localStorage !== 'undefined') {
          if (id) localStorage.setItem('damyxia:active-module-id', id);
          else localStorage.removeItem('damyxia:active-module-id');
        }
      } catch (_) { /* noop */ }
      // 🟥 iter458 · Emitir evento damyxia:workspace-reset cuando el
      // Workspace queda VACÍO (moduleId=null). Cualquier consumer que
      // dependa de continuidad (chat context, cache local, indicadores)
      // puede escuchar este evento para reiniciar su estado.
      if (!id && typeof window !== 'undefined') {
        try {
          window.dispatchEvent(new CustomEvent('damyxia:workspace-reset', {
            detail: { reason: reason || 'user-close', source: source || 'active-module-handler' },
          }));
        } catch (_) { /* noop */ }
      }
    };
    window.addEventListener('damyxia:active-module-changed', handler);
    return () => window.removeEventListener('damyxia:active-module-changed', handler);
  }, []);

  // Listener "damyxia-open-internal-module": dispara desde MessageBubble
  // "Enviar" → activa InternalWorkspace. En mobile, además cambia el
  // mobileView a 'workspace' para que se vea el editor.
  useEffect(() => {
    const handler = () => {
      setForceInternalWorkspace(true);
      setMobileView('workspace');
    };
    window.addEventListener('damyxia-open-internal-module', handler);
    // Drenar deep-link flag escrito por OpenInternalWorkspaceRedirect (si
    // el evento se disparó antes de que este listener estuviese registrado).
    try {
      if (typeof sessionStorage !== 'undefined' &&
          sessionStorage.getItem('damyxia_open_internal_workspace') === '1') {
        sessionStorage.removeItem('damyxia_open_internal_workspace');
        setForceInternalWorkspace(true);
        setMobileView('workspace');
      }
    } catch (_) { /* noop */ }
    // 🟥 CUADRO NEGRO 2026-06 · WORKSPACE ÚNICO — Trabajo activo elegido en
    // /mis-trabajos: al volver al Workspace se abre ese trabajo (y sólo ese).
    try {
      const pending = typeof sessionStorage !== 'undefined'
        && sessionStorage.getItem('damyxia.activeWorkId');
      if (pending) {
        sessionStorage.removeItem('damyxia.activeWorkId');
        setForceInternalWorkspace(true);
        setMobileView('workspace');
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', {
            detail: { moduleId: pending },
          }));
        }, 150);
      }
    } catch (_) { /* noop */ }
    return () => window.removeEventListener('damyxia-open-internal-module', handler);
  }, []);

  return {
    mobileView,
    setMobileView,
    swipeHandlers,
    chatPanelExpanded,
    setChatPanelExpanded,
    workspaceExpanded,
    setWorkspaceExpanded,
    forceInternalWorkspace,
    setForceInternalWorkspace,
    showImageEditor,
    setShowImageEditor,
    activeModuleId,
  };
}
